'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'

interface BreadcrumbsScrollProps {
  children: React.ReactNode
  fadeFrom?: 'white' | 'cream' | 'navy'
}

export const BreadcrumbsScroll = ({ children, fadeFrom = 'white' }: BreadcrumbsScrollProps) => {
  const { t } = useTranslation()
  const fadeClass = fadeFrom === 'cream' ? 'from-cream' : fadeFrom === 'navy' ? 'from-navy' : 'from-white'
  const scrollRef = useRef<HTMLOListElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const updateFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeft(el.scrollLeft > 0)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateFades()
    const el = scrollRef.current
    if (!el) return
    const observer = new ResizeObserver(updateFades)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateFades])

  return (
    <nav
      aria-label={t('aria.breadcrumbNav', 'Breadcrumb navigation')}
      className="relative flex min-h-[24px] min-w-0 items-center"
    >
      {showLeft && (
        <div
          className={`pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-6 bg-gradient-to-r ${fadeClass} to-transparent`}
        />
      )}
      {showRight && (
        <div
          className={`pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-6 bg-gradient-to-l ${fadeClass} to-transparent`}
        />
      )}
      <ol
        ref={scrollRef}
        onScroll={updateFades}
        className="no-scrollbar flex min-w-0 list-none flex-nowrap overflow-x-auto"
      >
        {children}
      </ol>
    </nav>
  )
}
